import { mode } from '@chakra-ui/theme-tools';
var parts = ['container', 'icon', 'list', 'scroll', 'option'];
var sizes = {
  lg: {
    container: {
      height: 12
    },
    input: {
      fontSize: 'lg'
    }
  },
  md: {
    container: {
      height: 10
    },
    input: {
      fontSize: 'md'
    }
  },
  sm: {
    container: {
      height: 8
    },
    input: {
      fontSize: 'sm'
    }
  },
  xs: {
    container: {
      height: 6
    },
    input: {
      fontSize: 'xs'
    }
  }
};

function baseStyleContainer(props) {
  return {
    alignItems: 'center',
    appearance: 'none',
    bg: 'transparent',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    lineHeight: 'normal',
    paddingBottom: '1px',
    position: 'relative',
    px: 0,
    width: props.width,
    '&:focus-visible': {
      outline: 'none'
    },
    _disabled: {
      cursor: 'not-allowed',
      '> *': {
        opacity: 0.4
      }
    },
    _readOnly: {
      boxShadow: 'none !important',
      userSelect: 'all'
    },
    ' > option': {
      bg: mode('white', 'gray.800')(props),
      _hover: {
        bg: mode('fluidBlue', 'blue.200')(props)
      }
    }
  };
}

var variantOutline = {
  container: {
    bg: 'white',
    border: '1px',
    borderColor: 'gray.700',
    borderRadius: 'md',
    '&:focus, &:active': {
      borderColor: 'fluidBlue',
      boxShadow: 'none'
    },
    _invalid: {
      borderColor: 'red.500',
      _focus: {
        borderColor: 'red.500'
      }
    }
  },
  input: {
    pl: 1,
    bg: 'transparent',
    color: 'inherit',
    '&::placeholder': {
      color: 'inherit'
    }
  },
  value: {
    pl: 1,
    color: 'inherit',
    bg: 'transparent'
  },
  icon: {
    color: 'fluidBlue'
  }
};
var variantLight = {
  container: {
    bg: 'white',
    borderBottom: '1px',
    borderColor: 'gray.700',
    '&:focus, &:active': {
      borderColor: 'fluidBlue',
      boxShadow: 'none'
    },
    _invalid: {
      borderColor: 'red.500',
      '&:focus, &:active': {
        borderColor: 'red.500'
      }
    }
  },
  input: {
    bg: 'transparent',
    color: 'inherit',
    '&::placeholder': {
      color: 'inherit'
    }
  },
  value: {
    color: 'inherit',
    bg: 'transparent'
  },
  icon: {
    color: 'fluidBlue'
  }
};
var variantDark = {
  container: {
    bg: 'transparent',
    borderBottom: '1px',
    borderColor: 'blue.100',
    _focus: {
      borderColor: 'blue.100',
      boxShadow: 'none'
    },
    _invalid: {
      borderColor: 'red.300',
      _focus: {
        borderColor: 'red.500'
      }
    }
  },
  input: {
    color: 'blue.100',
    bg: 'transparent',
    '&::placeholder': {
      color: 'blue.100'
    }
  },
  value: {
    color: 'blue.100',
    bg: 'transparent',
    borderColor: 'blue.100'
  },
  icon: {
    color: 'blue.100'
  }
};

function baseStyleList(props) {
  return {
    bg: mode('white', 'gray.800')(props),
    color: 'inherit',
    minW: 20,
    borderRadius: 'md',
    borderWidth: '1px',
    boxShadow: mode('md', 'dark-lg')(props),
    maxH: '18em',
    position: 'relative',
    px: 0,
    py: 0,
    zIndex: 'popover',
    backgroundColor: 'white',
    '&::before': {
      content: '""',
      height: 4,
      position: 'absolute',
      top: 0,
      width: 'inherit',
      bg: {
        base: 'linear-gradient(to bottom, white, rgba(255, 255, 255, 0))'
      }
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      width: 'inherit',
      height: 4,
      bg: {
        base: 'linear-gradient(to top, white, rgba(255, 255, 255, 0))'
      }
    }
  };
}

var baseStyleScroll = {
  borderBottom: 'inset .5rem white',
  color: 'inherit',
  maxH: '18em',
  overflowY: 'auto',
  scrollBehavior: 'smooth',
  paddingTop: 4,
  paddingBottom: 0,
  textAlign: 'center',
  width: '100%'
};
var baseStyleInput = {
  width: '100%',
  _disabled: {
    opacity: 0.5
  },
  _focus: {
    outline: 'none'
  },
  _focusWithin: {
    outline: 'none'
  }
};

function baseStyleOption(props) {
  return {
    alignItems: 'center',
    py: '0.4rem',
    px: '0.8rem',
    textDecoration: 'none',
    color: 'inherit',
    userSelect: 'none',
    display: 'flex',
    width: 'full',
    flex: '0 0 auto',
    outline: 0,
    borderRadius: '0',
    transition: 'background 50ms ease-in 0s',
    bg: 'white',
    '&:focus, &:hover': {
      bg: mode('gray.100', 'gray.100')(props),
      color: 'black'
    },
    _active: {
      bg: mode('fluidBlue', 'blue.200')(props),
      color: mode('white', 'blue.200')(props),
      '> svg': {
        color: mode('white', 'blue.200')(props)
      }
    },
    _disabled: {
      bg: mode('white', 'whiteAlpha.100')(props),
      color: mode('gray.100', 'gray.100')(props),
      opacity: 0.4,
      cursor: 'not-allowed'
    }
  };
}

function baseStyleValue(props) {
  return {
    alignItems: 'center',
    textDecoration: 'none',
    color: 'inherit',
    userSelect: 'none',
    display: 'flex',
    width: 'full',
    flex: '0 0 auto',
    outline: 0,
    borderRadius: '0',
    transition: 'background 50ms ease-in 0s',
    bg: 'white',
    _disabled: {
      bg: mode('white', 'whiteAlpha.100')(props),
      color: mode('gray.100', 'gray.100')(props),
      opacity: 0.4,
      cursor: 'not-allowed'
    }
  };
}

var variants = {
  dark: variantDark,
  light: variantLight,
  outline: variantOutline
};

var baseStyle = props => ({
  container: baseStyleContainer(props),
  scroll: baseStyleScroll,
  input: baseStyleInput,
  list: baseStyleList(props),
  option: baseStyleOption(props),
  value: baseStyleValue(props),
  icon: {
    h: '14px',
    ml: '.8em',
    width: '14px'
  }
});

var defaultProps = {
  size: 'md',
  width: '100%',
  fontSize: 'md',
  variant: 'light'
};
export default {
  parts,
  baseStyle,
  defaultProps,
  sizes,
  variants
};
//# sourceMappingURL=Select.js.map